import { useState, useEffect } from 'react';
import Select from 'react-select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';

import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

// Fixed items structure as per requirement (from the reference order sheet)
const FIXED_CATEGORY_COLUMNS = [
    { name: 'RICE-P VADI',    weights: ['50 Kg', '26 Kg', '10 Kg', '5 Kg'] },
    { name: 'RICE-P UNDA',    weights: ['50 Kg', '26 Kg', '10 Kg', '5 Kg'] },
    { name: 'RICE-P JAYA',    weights: ['50 Kg', '10 Kg', '5 Kg'] },
    { name: 'RICE-P SUREKHA', weights: ['50 Kg', '10 Kg', '5 Kg'] },
    { name: 'RICE-P KURUVA',  weights: ['50 Kg', '10 Kg', '5 Kg'] },
    { name: 'P RAW RICE IR8', weights: ['26 Kg'] },
    { name: 'RICE-P PONNI',   weights: ['26 Kg'] },
    { name: 'SUGAR',          weights: ['50 Kg'] },
];

// helper - numeric part of weight label ("50 Kg" -> 50)
const weightToKg = (w) => {
    const m = String(w).match(/[\d.]+/);
    return m ? parseFloat(m[0]) : 0;
};

function RouteTemplate() {
    const { isAuthenticated, companyId, token, logout } = useAuth();
    const [routeOptions, setRouteOptions] = useState([]);
    const [selectedRoute, setSelectedRoute] = useState(null);
    const [templateData, setTemplateData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [orderFrom, setOrderFrom] = useState('');
    const [asmSoSe, setAsmSoSe] = useState('');

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
        }
    }, [isAuthenticated, token, logout]);

    useEffect(() => {
        fetchRouteTypes();
    }, []);

    // ISO date without timezone shifting (yyyy-mm-dd) using local date parts
    const toISODateLocal = (d) => {
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    };

    const addDays = (dateStr, n) => {
        if (!dateStr) return '';
        const [y, m, d] = dateStr.split('-').map(Number);
        const dt = new Date(y, m - 1, d);
        dt.setDate(dt.getDate() + n);
        return toISODateLocal(dt);
    };

    const formatDateDMY = (d) => {
        if (!d) return '';
        try {
            if (/^\d{4}-\d{2}-\d{2}$/.test(d)) {
                const [y, m, day] = d.split('-');
                return `${day}-${m}-${y}`;
            }
            const dt = new Date(d);
            const day = String(dt.getDate()).padStart(2, '0');
            const mon = String(dt.getMonth() + 1).padStart(2, '0');
            return `${day}-${mon}-${dt.getFullYear()}`;
        } catch { return ''; }
    };

    // Compute dates fresh at export time from today's date
    const computeExportDates = () => {
        const from = toISODateLocal(new Date()); // current date
        const to = addDays(from, 6);              // + 6 days
        const supply = addDays(from, 7);          // + 7 days
        return { from, to, supply };
    };

    const fetchRouteTypes = async () => {
        try {
            const response = await fetchData('RouteApi', `/${companyId}`, token);
            if (response.responseCode === '1111') {
                const options = response.response_data.map(route => ({
                    value: route.id,
                    label: route.name
                }));
                setRouteOptions(options);
            }
        } catch (error) {
            console.error('Error fetching routes:', error);
            if (error.response && error.response.status === 401) {
                logout();
            }
        }
    };

    const handleRouteChange = async (selectedOption) => {
        setSelectedRoute(selectedOption);
        if (selectedOption) {
            await fetchTemplateData(selectedOption.value);
        } else {
            setTemplateData([]);
        }
    };

    // Fetch customers with item template by route + all route customers for balance
    const fetchTemplateData = async (routeId) => {
        try {
            setLoading(true);

            const [templateResponse, customerResponse] = await Promise.all([
                fetchData('RouteExcelTemplateApi', `/${companyId}/${routeId}`, token),
                fetchData('CustomerApi', `/${companyId}/${routeId}`, token)
            ]);

            let templateList = [];
            if (templateResponse.responseCode === '1111') {
                templateList = templateResponse.response_data || [];
            }

            let allCustomers = [];
            if (customerResponse.responseCode === '1111') {
                allCustomers = customerResponse.response_data || [];
            }

            const templateMap = {};
            templateList.forEach(item => {
                const key = item.CustomerName?.trim()?.toLowerCase() || item.CustomerId;
                if (key) templateMap[key] = item;
            });

            const mergedData = [...templateList];

            allCustomers.forEach(cust => {
                const custName = (cust.name || cust.CustomerName || '').trim().toLowerCase();
                const custId = cust.id || cust.CustomerId;
                const existsInTemplate = templateMap[custName] ||
                    templateList.some(t => (t.CustomerId && t.CustomerId === custId));

                const lastSale = cust.lastsaledate || cust.lastSaleDate || cust.LastSaleDate || cust.LastSale || '';

                if (!existsInTemplate) {
                    mergedData.push({
                        CustomerName: cust.name || cust.CustomerName || '',
                        CustomerId: cust.id || cust.CustomerId,
                        RouteSequenceNo: cust.routeSequenceNo || cust.RouteSequenceNo || '999',
                        OpeningBalance: cust.openingBalance || cust.OpeningBalance || 0,
                        ClosingBalance: cust.closingBalance || cust.ClosingBalance || null,
                        ClosingBalanceType: cust.closingBalanceType || cust.ClosingBalanceType || '',
                        LastSaleDate: lastSale,
                        PaymentMode: cust.paymentType || cust.PaymentMode || '',
                        Remarks: cust.remarks || cust.Remarks || '',
                        Gst: cust.gst || cust.Gst || '',
                        ItemList: []
                    });
                } else {
                    // enrich existing template row with remarks / gst / last sale date if missing
                    const existing = mergedData.find(m =>
                        (m.CustomerId && m.CustomerId === custId) ||
                        (m.CustomerName || '').trim().toLowerCase() === custName
                    );
                    if (existing) {
                        if (!existing.Remarks) existing.Remarks = cust.remarks || cust.Remarks || '';
                        if (!existing.Gst) existing.Gst = cust.gst || cust.Gst || '';
                        if (!existing.LastSaleDate) existing.LastSaleDate = lastSale;
                    }
                }
            });

            const sorted = mergedData.sort(
                (a, b) => (parseInt(a.RouteSequenceNo) || 0) - (parseInt(b.RouteSequenceNo) || 0)
            );
            setTemplateData(sorted);
        } catch (error) {
            console.error('Error fetching template data:', error);
            if (error.response && error.response.status === 401) {
                logout();
            }
            setTemplateData([]);
        } finally {
            setLoading(false);
        }
    };

    // Use fixed columns instead of dynamic API items
    const categoryColumns = FIXED_CATEGORY_COLUMNS;

    // Total number of item columns (used for bottom-summary spans)
    const totalItemColumns = categoryColumns.reduce((acc, c) => acc + c.weights.length, 0);

    // Opening balance total for Sub Total row
    const openingBalanceTotal = templateData.reduce((sum, cust) => {
        const val = cust.ClosingBalance != null
            ? Number(cust.ClosingBalance)
            : Number(cust.OpeningBalance) || 0;
        return sum + (isNaN(val) ? 0 : val);
    }, 0);

    const handleExportTempExcel = async (response_data = []) => {
        try {
            if (!Array.isArray(response_data) || response_data.length === 0) {
                return;
            }

            // Compute dates fresh at click time
            const { from: fromDate, to: toDate, supply: dateOfSupply } = computeExportDates();

            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet("Order Sheet");

            const thinBorder = {
                top: { style: "thin" },
                left: { style: "thin" },
                bottom: { style: "thin" },
                right: { style: "thin" },
            };

            const fixedHeaders = [
                "Shop Ord",
                "Customer Name",
                "Opening Balance",
                "Last Date of Sale",
                "Payment Mode",
            ];

            // Total columns = fixed(5) + item weights + remarks(1)
            const totalCols = fixedHeaders.length + totalItemColumns + 1;
            const lastColLetter = worksheet.getColumn(totalCols).letter;

            // ===== HEADER SECTION =====
            worksheet.mergeCells(`A1:${lastColLetter}1`);
            worksheet.getCell("A1").value = "ORDER SHEET";
            worksheet.getCell("A1").font = { bold: true, size: 14 };
            worksheet.getCell("A1").alignment = { horizontal: "center", vertical: "middle" };
            worksheet.getCell("A1").fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF3F3E3" } };

            // Row 2
            worksheet.mergeCells("A2:F2");
            worksheet.getCell("A2").value = `ROUTE: ${selectedRoute?.label || "N/A"}`;
            worksheet.getCell("A2").alignment = { horizontal: "left", vertical: "middle" };
            worksheet.mergeCells(`G2:${lastColLetter}2`);
            worksheet.getCell("G2").value = "Order Details";
            worksheet.getCell("G2").alignment = { horizontal: "center", vertical: "middle" };
            worksheet.getCell("G2").font = { bold: true };

            // Row 3
            worksheet.mergeCells("A3:F3");
            worksheet.getCell("A3").value = `Date From: ${formatDateDMY(fromDate)} To ${formatDateDMY(toDate)}`;
            worksheet.getCell("A3").alignment = { horizontal: "left", vertical: "middle" };
            worksheet.mergeCells(`G3:${lastColLetter}3`);
            worksheet.getCell("G3").value = "CATEGORY & VARIANT";
            worksheet.getCell("G3").alignment = { horizontal: "center", vertical: "middle" };
            worksheet.getCell("G3").font = { bold: true };

            // Row 4
            worksheet.mergeCells("A4:C4");
            worksheet.getCell("A4").value = `ASM/SO/SE: ${asmSoSe || ''}`;
            worksheet.getCell("A4").alignment = { horizontal: "left", vertical: "middle" };
            worksheet.mergeCells("D4:H4");
            worksheet.getCell("D4").value = `Date of Supply: ${formatDateDMY(dateOfSupply)}`;
            worksheet.getCell("D4").alignment = { horizontal: "left", vertical: "middle" };
            worksheet.mergeCells(`I4:${lastColLetter}4`);
            worksheet.getCell("I4").value = orderFrom ? `Order From: ${orderFrom}` : '';
            worksheet.getCell("I4").alignment = { horizontal: "left", vertical: "middle" };

            for (let i = 1; i <= 4; i++) {
                for (let j = 1; j <= totalCols; j++) {
                    const cell = worksheet.getRow(i).getCell(j);
                    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF3F3E3" } };
                    cell.border = thinBorder;
                }
            }

            // ===== HEADER ROWS (row5 & row6) =====
            const headerStartRow = 5;
            const headerRow1 = [...fixedHeaders];
            categoryColumns.forEach((col) => {
                headerRow1.push(col.name);
                for (let i = 1; i < col.weights.length; i++) headerRow1.push(null);
            });
            headerRow1.push("Remarks");
            worksheet.insertRow(headerStartRow, headerRow1);

            const headerRow2 = [
                ...fixedHeaders.map(() => null),
                ...categoryColumns.flatMap((col) => col.weights.map((w) => `${w}`)),
                null,
            ];
            worksheet.insertRow(headerStartRow + 1, headerRow2);

            // Merge fixed headers vertically
            for (let c = 1; c <= fixedHeaders.length; c++) {
                worksheet.mergeCells(headerStartRow, c, headerStartRow + 1, c);
            }
            // Merge remarks vertically
            worksheet.mergeCells(headerStartRow, totalCols, headerStartRow + 1, totalCols);
            worksheet.getCell(headerStartRow, totalCols).value = "Remarks";

            // Merge category cells horizontally
            let colStart = fixedHeaders.length + 1;
            categoryColumns.forEach((col) => {
                const colEnd = colStart + col.weights.length - 1;
                worksheet.mergeCells(headerStartRow, colStart, headerStartRow, colEnd);
                worksheet.getCell(headerStartRow, colStart).alignment = { horizontal: "center", vertical: "middle" };
                worksheet.getCell(headerStartRow, colStart).font = { bold: true };
                colStart = colEnd + 1;
            });

            [worksheet.getRow(headerStartRow), worksheet.getRow(headerStartRow + 1)].forEach((row) => {
                row.font = { bold: true };
                row.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
                row.eachCell((cell) => (cell.border = thinBorder));
            });

            // ===== CUSTOMER ROWS =====
            let dataStartRow = headerStartRow + 2;
            const sortedData = [...response_data].sort(
                (a, b) => (parseInt(a.RouteSequenceNo) || 0) - (parseInt(b.RouteSequenceNo) || 0)
            );
            sortedData.forEach((cust) => {
                const closingVal = cust.ClosingBalance != null ? Number(cust.ClosingBalance) : null;
                const openingVal = cust.OpeningBalance != null ? Number(cust.OpeningBalance) : null;
                let balanceDisplay = '';
                if (closingVal != null && !isNaN(closingVal) && closingVal !== 0) {
                    balanceDisplay = `${closingVal.toFixed(2)} ${cust.ClosingBalanceType ?? ''}`.trim();
                } else if (openingVal != null && !isNaN(openingVal) && openingVal !== 0) {
                    balanceDisplay = openingVal.toFixed(2);
                }

                const row = [
                    cust.RouteSequenceNo,
                    cust.CustomerName || "",
                    balanceDisplay,
                    cust.LastSaleDate ? new Date(cust.LastSaleDate).toLocaleDateString("en-GB") : "",
                    cust.PaymentMode || "",
                ];

                categoryColumns.forEach((col) => {
                    col.weights.forEach(() => row.push(""));
                });

                const remarksText = cust.Remarks
                    ? cust.Remarks
                    : cust.Gst
                        ? `GSTIN: ${cust.Gst}`
                        : "";
                row.push(remarksText);
                const addedRow = worksheet.insertRow(dataStartRow++, row);
                addedRow.getCell(row.length).alignment = { wrapText: true, vertical: 'middle' };
                addedRow.eachCell((cell) => (cell.border = thinBorder));
            });

            // ===== BOTTOM SUMMARY ROWS =====
            // Row 1: Sub Total (item cells blank for manual fill)
            const subTotalRow = [
                null,
                "Sub Total:-",
                openingBalanceTotal > 0 ? openingBalanceTotal.toFixed(1) : "",
                null,
                null,
            ];
            categoryColumns.forEach((col) => col.weights.forEach(() => subTotalRow.push("")));
            subTotalRow.push(null);
            const stRow = worksheet.insertRow(dataStartRow++, subTotalRow);
            stRow.font = { bold: true };
            stRow.alignment = { horizontal: 'center', vertical: 'middle' };
            stRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
                if (colNumber <= totalCols) cell.border = thinBorder;
            });
            worksheet.mergeCells(dataStartRow - 1, 1, dataStartRow - 1, 2);
            worksheet.getCell(dataStartRow - 1, 1).value = "Sub Total:-";
            worksheet.getCell(dataStartRow - 1, 1).alignment = { horizontal: 'right', vertical: 'middle' };
            worksheet.getCell(dataStartRow - 1, 1).font = { bold: true };

            // Row 2: Total Quantity - Actual Order (Bags)
            const bagsRow = [
                "Total Quantity - Actual Order (Bags):-",
            ];
            for (let i = 1; i < fixedHeaders.length; i++) bagsRow.push(null);
            categoryColumns.forEach((col) => col.weights.forEach(() => bagsRow.push("")));
            bagsRow.push(null);
            const bRow = worksheet.insertRow(dataStartRow++, bagsRow);
            bRow.font = { bold: true };
            worksheet.mergeCells(dataStartRow - 1, 1, dataStartRow - 1, fixedHeaders.length);
            worksheet.getCell(dataStartRow - 1, 1).value = "Total Quantity - Actual Order (Bags):-";
            worksheet.getCell(dataStartRow - 1, 1).alignment = { horizontal: 'left', vertical: 'middle' };
            worksheet.getCell(dataStartRow - 1, 1).font = { bold: true };
            bRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
                if (colNumber <= totalCols) cell.border = thinBorder;
            });

            // Row 3: Total Quantity - Actual Order (Kgs)
            const kgsRow = [
                "Total Quantity - Actual Order (Kgs):-",
            ];
            for (let i = 1; i < fixedHeaders.length; i++) kgsRow.push(null);
            categoryColumns.forEach((col) => col.weights.forEach(() => kgsRow.push("")));
            kgsRow.push(null);
            const kRow = worksheet.insertRow(dataStartRow++, kgsRow);
            kRow.font = { bold: true };
            worksheet.mergeCells(dataStartRow - 1, 1, dataStartRow - 1, fixedHeaders.length);
            worksheet.getCell(dataStartRow - 1, 1).value = "Total Quantity - Actual Order (Kgs):-";
            worksheet.getCell(dataStartRow - 1, 1).alignment = { horizontal: 'left', vertical: 'middle' };
            worksheet.getCell(dataStartRow - 1, 1).font = { bold: true };
            kRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
                if (colNumber <= totalCols) cell.border = thinBorder;
            });

            // ===== COLUMN WIDTHS =====
            worksheet.columns.forEach((col, i) => {
                if (i === 0) col.width = 8;
                else if (i === 1) col.width = 30;
                else col.width = 8;
            });

            worksheet.pageSetup = {
                paperSize: 9,
                orientation: "landscape",
                fitToPage: true,
                fitToWidth: 1,
                fitToHeight: 0,
                margins: {
                    left: 0.3,
                    right: 0.3,
                    top: 0.5,
                    bottom: 0.5,
                    header: 0.1,
                    footer: 0.1,
                },
            };

            const buffer = await workbook.xlsx.writeBuffer();
            saveAs(new Blob([buffer]), `OrderSheet_${selectedRoute?.label || "Route"}.xlsx`);
        } catch (error) {
            console.error("❌ Excel Export Failed:", error);
            alert("Error while exporting Excel. Check console for details.");
        }
    };

    return (
        <div className="w-full container mt-6 mx-auto px-4">
            <div className="w-full mb-4">
                <h6
                    className="uppercase tracking-wide text-black flex-grow linkcolor"
                    style={{ fontFamily: 'Georgia, Cambria, "Times New Roman", Times, serif' }}
                >
                    Route Template
                </h6>
            </div>

            {/* Top Controls: Order From, ASM/SO/SE, Date of Supply, Route, Download */}
            <div
                className="w-full mb-4"
                style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}
            >
                
                <div style={{ minWidth: '260px', flex: 1, position: 'relative', zIndex: 10 }}>
                    <Select
                        options={routeOptions}
                        value={selectedRoute}
                        onChange={handleRouteChange}
                        placeholder="Select Route..."
                        isClearable
                        menuPortalTarget={document.body}
                        styles={{
                            control: (base) => ({
                                ...base,
                                borderColor: '#ccc',
                                boxShadow: 'none',
                                '&:hover': { borderColor: 'teal' }
                            }),
                            menuPortal: (base) => ({
                                ...base,
                                zIndex: 9999,
                            }),
                        }}
                    />
                </div>

                {templateData.length > 0 && (
                    <Button
                        variant="contained"
                        onClick={() => handleExportTempExcel(templateData)}
                        style={{
                            background: 'linear-gradient(to right, #2e7d32, #4caf50)',
                            borderRadius: '50px',
                            padding: '8px 20px',
                            color: 'white',
                            fontWeight: 'bolder',
                            textTransform: 'none',
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
                            maxHeight: '38px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <FileDownloadIcon style={{ marginRight: '5px' }} /> Download Excel
                    </Button>
                )}
            </div>

            {/* Loading */}
            {loading && (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                    <CircularProgress />
                </div>
            )}

            {/* Data Table Display */}
            {!loading && templateData.length > 0 && (
                <div style={{ width: '100%', overflowX: 'auto' }}>
                    <TableContainer component={Paper} sx={{ maxHeight: '520px', overflowY: 'auto' }}>
                        <Table size="small" stickyHeader sx={{ minWidth: 1200 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell
                                        rowSpan={2}
                                        sx={{ backgroundColor: 'teal !important', color: 'white', fontWeight: 'bold', fontSize: '12px', padding: '6px', position: 'sticky', top: 0, zIndex: 4, whiteSpace: 'nowrap' }}
                                    >
                                        Shop Ord
                                    </TableCell>
                                    <TableCell
                                        rowSpan={2}
                                        sx={{ backgroundColor: 'teal !important', color: 'white', fontWeight: 'bold', fontSize: '12px', padding: '6px', minWidth: '100px', position: 'sticky', top: 0, zIndex: 4, whiteSpace: 'nowrap' }}
                                    >
                                        Customer Name
                                    </TableCell>
                                    <TableCell
                                        rowSpan={2}
                                        sx={{ backgroundColor: 'teal !important', color: 'white', fontWeight: 'bold', fontSize: '12px', padding: '6px', position: 'sticky', top: 0, zIndex: 4, whiteSpace: 'nowrap' }}
                                    >
                                        Opening Balance
                                    </TableCell>
                                    <TableCell
                                        rowSpan={2}
                                        sx={{ backgroundColor: 'teal !important', color: 'white', fontWeight: 'bold', fontSize: '12px', padding: '6px', position: 'sticky', top: 0, zIndex: 4, whiteSpace: 'nowrap' }}
                                    >
                                        Last Date of Sale
                                    </TableCell>
                                    <TableCell
                                        rowSpan={2}
                                        sx={{ backgroundColor: 'teal !important', color: 'white', fontWeight: 'bold', fontSize: '12px', padding: '6px', position: 'sticky', top: 0, zIndex: 4, whiteSpace: 'nowrap' }}
                                    >
                                        Payment Mode
                                    </TableCell>
                                    {categoryColumns.map((cat) => (
                                        <TableCell
                                            key={cat.name}
                                            colSpan={cat.weights.length}
                                            align="center"
                                            sx={{ backgroundColor: '#2e7d32 !important', color: 'white', fontWeight: 'bold', fontSize: '12px', padding: '6px', position: 'sticky', top: 0, zIndex: 3, whiteSpace: 'nowrap' }}
                                        >
                                            {cat.name}
                                        </TableCell>
                                    ))}
                                    <TableCell
                                        rowSpan={2}
                                        sx={{ backgroundColor: 'teal !important', color: 'white', fontWeight: 'bold', fontSize: '12px', padding: '6px', position: 'sticky', top: 0, zIndex: 4, whiteSpace: 'nowrap' }}
                                    >
                                        Remarks
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    {categoryColumns.flatMap((cat) =>
                                        cat.weights.map((weight) => (
                                            <TableCell
                                                key={`${cat.name}-${weight}`}
                                                align="center"
                                                sx={{ backgroundColor: '#43a047 !important', color: 'white', fontWeight: 'bold', fontSize: '11px', padding: '4px', position: 'sticky', top: '36px', zIndex: 3, whiteSpace: 'nowrap' }}
                                            >
                                                {weight}
                                            </TableCell>
                                        ))
                                    )}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {templateData.map((cust, idx) => (
                                    <TableRow key={idx} hover>
                                        <TableCell sx={{ fontSize: '12px', padding: '5px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                                            {cust.RouteSequenceNo}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '12px', padding: '5px', whiteSpace: 'nowrap' }}>
                                            {cust.CustomerName || ""}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '12px', padding: '5px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                            {(() => {
                                                const cb = cust.ClosingBalance != null ? Number(cust.ClosingBalance) : null;
                                                const ob = cust.OpeningBalance != null ? Number(cust.OpeningBalance) : null;
                                                if (cb != null && !isNaN(cb) && cb !== 0) {
                                                    return `${cb.toFixed(2)} ${cust.ClosingBalanceType ?? ''}`.trim();
                                                }
                                                if (ob != null && !isNaN(ob) && ob !== 0) {
                                                    return ob.toFixed(2);
                                                }
                                                return '';
                                            })()}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '12px', padding: '5px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                                            {cust.LastSaleDate ? new Date(cust.LastSaleDate).toLocaleDateString("en-GB") : ""}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '12px', padding: '5px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                                            {cust.PaymentMode || ""}
                                        </TableCell>
                                        {categoryColumns.flatMap((cat) =>
                                            cat.weights.map((weight) => (
                                                <TableCell
                                                    key={`${idx}-${cat.name}-${weight}`}
                                                    sx={{ fontSize: '12px', padding: '5px', textAlign: 'center', whiteSpace: 'nowrap' }}
                                                >
                                                    {""}
                                                </TableCell>
                                            ))
                                        )}
                                        <TableCell sx={{ fontSize: '12px', padding: '5px', whiteSpace: 'normal', minWidth: '160px', maxWidth: '220px' }}>
                                            {cust.Remarks
                                                ? cust.Remarks
                                                : cust.Gst
                                                    ? `GSTIN: ${cust.Gst}`
                                                    : ''}
                                        </TableCell>
                                    </TableRow>
                                ))}

                                {/* ===== BOTTOM SUMMARY ROWS ===== */}
                                {/* Sub Total */}
                                <TableRow sx={{ backgroundColor: '#f5f5dc' }}>
                                    <TableCell colSpan={2} sx={{ fontSize: '12px', padding: '6px', fontWeight: 'bold', textAlign: 'right' }}>
                                        Sub Total:-
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '12px', padding: '6px', fontWeight: 'bold', textAlign: 'right' }}>
                                        {openingBalanceTotal > 0 ? openingBalanceTotal.toFixed(1) : ''}
                                    </TableCell>
                                    <TableCell />
                                    <TableCell />
                                    {categoryColumns.flatMap((cat) =>
                                        cat.weights.map((weight) => (
                                            <TableCell
                                                key={`sub-${cat.name}-${weight}`}
                                                sx={{ fontSize: '12px', padding: '6px', fontWeight: 'bold', textAlign: 'center' }}
                                            />
                                        ))
                                    )}
                                    <TableCell />
                                </TableRow>

                                {/* Total Quantity - Actual Order (Bags) */}
                                <TableRow sx={{ backgroundColor: '#f5f5dc' }}>
                                    <TableCell colSpan={5} sx={{ fontSize: '12px', padding: '6px', fontWeight: 'bold' }}>
                                        Total Quantity - Actual Order (Bags):-
                                    </TableCell>
                                    {categoryColumns.flatMap((cat) =>
                                        cat.weights.map((weight) => (
                                            <TableCell
                                                key={`bags-${cat.name}-${weight}`}
                                                sx={{ fontSize: '12px', padding: '6px', textAlign: 'center' }}
                                            >
                                                {""}
                                            </TableCell>
                                        ))
                                    )}
                                    <TableCell />
                                </TableRow>

                                {/* Total Quantity - Actual Order (Kgs) */}
                                <TableRow sx={{ backgroundColor: '#f5f5dc' }}>
                                    <TableCell colSpan={5} sx={{ fontSize: '12px', padding: '6px', fontWeight: 'bold' }}>
                                        Total Quantity - Actual Order (Kgs):-
                                    </TableCell>
                                    {categoryColumns.flatMap((cat) =>
                                        cat.weights.map((weight) => (
                                            <TableCell
                                                key={`kgs-${cat.name}-${weight}`}
                                                sx={{ fontSize: '12px', padding: '6px', textAlign: 'center' }}
                                            >
                                                {""}
                                            </TableCell>
                                        ))
                                    )}
                                    <TableCell />
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>
            )}

            {/* No Data Message */}
            {!loading && selectedRoute && templateData.length === 0 && (
                <div style={{ textAlign: 'center', padding: '30px', color: '#888' }}>
                    No template data found for the selected route.
                </div>
            )}
        </div>
    );
}

// exported helper (silence lint - retained for potential future use with dynamic weights)
// eslint-disable-next-line no-unused-vars
const _weightToKgHelper = weightToKg;

export default RouteTemplate;
   